'use client'

import { HTTPError } from 'ky'
import { CalendarDays, Gift, LoaderCircle, Star } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { AGENDOR_CONTACT_ORIGIN } from '@/helpers/postgraduate-agendor-funnel'
import { paymentInstallments } from '@/helpers/payment-installments'
import { priceFormat } from '@/helpers/price-format'
import type { GetProductOutput } from '@/http/zamply/products/get-product-schema'
import type { ProfileType } from '@/types/profile-type'
import { sendEventBrevo } from '@/actions/send-event-brevo'
import { getProfileAction } from '@/actions/user/get-profile-action'
import { trackEvent } from '@/components/@analitycs/trackEvent'
// import SignInModal from '@/components/sign-out-modals/sign-in-modal'
import { Button } from '@/components/ui/button'
import { useAuthStatus } from '@/hooks/use-auth-status'
import { useCreateEnrolment } from '@/hooks/use-create-enrollment'
import { bff_client } from '@/lib/apiClient'
import medalIcon from '@/public/icons/medal.svg'
// import EnrollmentCollectionsModal from '../info/enrollment-modal/modal-enrollment-collections'
import ModalBankSlip from './modal-bank-slip'

interface PurchaseCardGroupProps {
  graduation: GetProductOutput
  isEstetus?: boolean
}

interface EnrolmentDataType {
  product_id: string
  product_external_id?: number
  workload_value?: number
  workload_id: string
  period_id: string
}

export default function PurchaseCardGroup({
  graduation,
  isEstetus,
}: PurchaseCardGroupProps) {
  const router = useRouter()
  const { handleSubmit } = useCreateEnrolment()
  const status = useAuthStatus()
  const [profile, setProfile] = useState<ProfileType | null>(null)
  const [isLoading, setIsLoading] = useState<'credit' | 'pix' | null>(null)
  const [notLoggedIn, setNotLoggedIn] = useState(false)
  const [enrolmentData, setEnrolmentData] = useState<EnrolmentDataType | null>(
    null
  )

  // Fetch profile when user is authenticated
  useEffect(() => {
    async function fetchProfile() {
      if (status === 'authenticated') {
        try {
          const result = await getProfileAction()
          if ('data' in result && result.data) {
            setProfile(result.data as ProfileType)
          } else {
            setProfile(null)
          }
        } catch (error) {
          console.error('Failed to fetch profile:', error)
        }
      } else {
        setProfile(null)
      }
    }

    fetchProfile()
  }, [status])

  async function handleEnrolment(buttonType: 'credit' | 'pix') {
    setIsLoading(buttonType)

    const payload = {
      product_id: graduation.reference,
      product_external_id: Number(graduation.external_id),
      workload_value: Number(graduation.workloads[0].workload_value),
      workload_id: graduation.workloads[0].reference,
      period_id: graduation.periods[0].reference,
    }

    setEnrolmentData(payload)

    if (status === 'unauthenticated') {
      setNotLoggedIn(true)
      setIsLoading(null)

      return
    }

    try {
      const enrolmentAlredyExists = await bff_client.get(
        `enrolments/exists/${graduation.reference}`
      )

      if (profile && profile.email) {
        await sendEventBrevo({
          email: profile.email,
          event: 'interest_pos',
          properties: {
            course_name: graduation.name,
            nome: profile?.full_name ?? '',
            whatsapp: profile?.phone_number,
          },
          eventdata: {
            data: {
              course_id: graduation.reference,
              course_name: graduation.name,
              email: profile?.email ?? '',
              nome: profile?.full_name ?? '',
              whatsapp: profile?.phone_number,
            },
          },
        })
      }

      if (enrolmentAlredyExists.ok) {
        router.push(`/app/sala/curso/${graduation.external_id}`)
      }
    } catch (error) {
      if (error instanceof HTTPError) {
        const status = error.response.status

        if (status === 404) {
          const response: any = await handleSubmit(payload)

          const eventData = {
            event: 'vitrine',
            eventName: 'subscription',
            customDataObject: {
              environment: 'Vitrine',
              value: graduation.amount,
              subscription_id: response.object.id,
              external_id: profile?.reference ?? profile?.external_id,
              curso_id: graduation.reference ?? graduation.external_id,
              curso_nome: graduation.name,
              area_nome: 'Pós-graduação',
              carga_horaria: graduation.workloads[0].workload_value,
            },
            dedupeId: response.object.id,
          }

          if (!response?.error) {
            trackEvent(eventData)
            router.prefetch('/checkout/payment/resume')
            return router.push('/checkout/payment/resume')
          }
        } else {
          console.error('Error: ', status)
        }
      } else {
        console.error('Error: ', error)
      }
    }

    setIsLoading(null)
  }

  const installments = paymentInstallments(graduation.amount ?? '')

  return (
    <div className="mt-6 flex flex-wrap items-start gap-x-8 gap-y-4 lg:flex-nowrap">
      <div className="w-full rounded-1xl border border-[#DFCEFF] px-6 py-5">
        <h4 className="font-medium text-2xl text-[#2E2E2E]">
          Cartão de Crédito
        </h4>
        <div className="mt-3">
          <div className="rounded-t-lg rounded-b-[2px] bg-[#5EFF9A] py-1 text-center">
            <span className="font-bold text-[#000000] text-base">
              50% de Desconto
            </span>
          </div>
          <span className="mt-1 font-normal text-[#9082A3] text-sm">
            De{' '}
            <span className="line-through">
              {priceFormat(Number(graduation?.amount) * 2)}
            </span>{' '}
            por <strong>{priceFormat(graduation.amount ?? '')}</strong>
          </span>
        </div>
        <div className="mt-3 flex flex-col gap-y-1">
          <small className="font-semibold text-[#9082A3] text-sm">
            Em até {installments.quantity}x de
          </small>
          <span className="font-semibold text-5xl text-[#2E2E2E]">
            {priceFormat(installments.value)}
          </span>
          <small className="font-semibold text-[#9082A3] text-sm">
            Sem juros no Cartão
          </small>
        </div>
        <div className="mt-5 space-y-3">
          <span className="flex items-center gap-1 font-normal text-[#583979] text-sm">
            <CalendarDays className="size-5 text-[#D2A0FE]" />
            Conclusão a partir de 4 meses
          </span>
          <span className="flex items-center gap-1 font-normal text-[#583979] text-sm">
            <Image alt="" height={20} src={medalIcon} width={20} /> Autorizada e
            reconhecida pelo MEC.
          </span>
        </div>
        {graduation.external_redirect_url ? (
          <Button
            className="mt-10 h-12 rounded-full border border-black text-base text-black"
            onClick={() =>
              router.push(
                `/trust?redirect=${encodeURIComponent(graduation.external_redirect_url ?? '')}`
              )
            }
            variant="outline"
          >
            MATRICULE-SE COM CARTÃO
          </Button>
        ) : (
          <ModalBankSlip
            contactOrigin={AGENDOR_CONTACT_ORIGIN.QUERO_ME_INSCREVER}
            courseAmount={graduation.amount}
            courseId={graduation.reference}
            courseName={graduation.name}
            title="Possui interesse no Cartão? Entraremos em contato."
          >
            <Button
              className="mt-10 h-12 rounded-full border border-black text-base text-black"
              disabled={isLoading === 'credit' || isLoading === 'pix'}
              onClick={() => handleEnrolment('credit')}
              variant="outline"
            >
              {isLoading === 'credit' ? (
                <LoaderCircle className="size-36 animate-spin text-primary-300" />
              ) : (
                'MATRICULE-SE COM CARTÃO'
              )}
            </Button>
          </ModalBankSlip>
        )}
      </div>
      <div className="w-full rounded-1xl border border-[#DFCEFF] px-6 py-5">
        <h4 className="font-medium text-2xl text-[#2E2E2E]">PIX à vista</h4>
        <div className="mt-3">
          <div className="rounded-t-lg rounded-b-[2px] bg-[#5EFF9A] py-1 text-center">
            <span className="font-bold text-[#000000] text-base">
              50% de Desconto
            </span>
          </div>
          <span className="mt-1 font-normal text-[#9082A3] text-sm">
            De{' '}
            <span className="line-through">
              {priceFormat(Number(graduation?.amount) * 2)}
            </span>{' '}
            por <strong>{priceFormat(graduation.amount ?? '')}</strong>
          </span>
        </div>
        <div className="mt-3 flex flex-col gap-y-1">
          <span className="font-semibold text-5xl text-[#2E2E2E]">
            {priceFormat(graduation.amount ?? '')}
          </span>
        </div>
        <div className="mt-5 space-y-3">
          <span className="flex items-center gap-1 font-normal text-[#583979] text-sm">
            <Gift className="size-5 text-[#D2A0FE]" />
            Curso Bônus Exclusivo
          </span>
          <span className="flex items-center gap-1 font-normal text-[#583979] text-sm">
            <Star className="size-5 text-[#D2A0FE]" /> Formação profissional
          </span>
          <span className="flex items-center gap-1 font-normal text-[#583979] text-sm">
            <CalendarDays className="size-5 text-[#D2A0FE]" /> Conclusão a
            partir de 4 meses
          </span>
          <span className="flex items-center gap-1 font-normal text-[#583979] text-sm">
            <Image alt="" height={20} src={medalIcon} width={20} /> Autorizada e
            reconhecida pelo MEC.
          </span>
        </div>
        {graduation.external_redirect_url ? (
          <Button
            className="mt-10 h-12 rounded-full border border-black text-base text-black"
            onClick={() =>
              router.push(
                `/trust?redirect=${encodeURIComponent(graduation.external_redirect_url ?? '')}`
              )
            }
            variant="outline"
          >
            MATRICULE-SE COM PIX
          </Button>
        ) : (
          <ModalBankSlip
            contactOrigin={AGENDOR_CONTACT_ORIGIN.QUERO_ME_INSCREVER}
            courseAmount={graduation.amount}
            courseId={graduation.reference}
            courseName={graduation.name}
            title="Possui interesse no PIX? Entraremos em contato."
          >
            <Button
              className="mt-10 h-12 rounded-full border border-black text-base text-black"
              disabled={isLoading === 'credit' || isLoading === 'pix'}
              onClick={() => handleEnrolment('pix')}
              variant="outline"
            >
              {isLoading === 'pix' ? (
                <LoaderCircle className="size-36 animate-spin text-primary-300" />
              ) : (
                'MATRICULE-SE COM PIX'
              )}
            </Button>
          </ModalBankSlip>
        )}
      </div>
      <div className="w-full rounded-1xl border border-[#DFCEFF] px-6 py-5">
        <h4 className="font-medium text-2xl text-[#2E2E2E]">
          Boleto Parcelado
        </h4>
        <div className="mt-3">
          <strong className="mt-1 text-[#9082A3] text-sm">
            Parcelado no Boleto em até 3x com condições a partir de
          </strong>
        </div>
        <div className="mt-3 flex flex-col gap-y-1">
          <span className="font-semibold text-5xl text-[#2E2E2E]">
            R$ 214,44
          </span>
          <small className="font-semibold text-[#9082A3] text-sm">
            + entrada de R$ 275,70*
          </small>
        </div>

        <div className="mt-5 space-y-3">
          <span className="mt-1 font-medium text-[#9082A3] text-sm">
            *Inscreva-se e consulte condições
          </span>
          <span className="flex items-center gap-1 font-normal text-[#583979] text-sm">
            <Image alt="" height={20} src={medalIcon} width={20} /> Autorizada e
            reconhecida pelo MEC.
          </span>
        </div>
        {graduation.external_redirect_url ? (
          <Button
            className="mt-10 h-12 rounded-full border border-black text-base text-black"
            onClick={() =>
              router.push(
                `/trust?redirect=${encodeURIComponent(graduation.external_redirect_url ?? '')}`
              )
            }
            variant="outline"
          >
            MATRICULE-SE COM BOLETO
          </Button>
        ) : (
          <ModalBankSlip
            contactOrigin={AGENDOR_CONTACT_ORIGIN.QUERO_ME_INSCREVER}
            courseAmount={graduation.amount}
            courseId={graduation.reference}
            courseName={graduation.name}
            title="Possui interesse no Boleto Parcelado? Entraremos em contato."
          >
            <Button
              className="mt-10 h-12 rounded-full border border-black text-base text-black"
              variant="outline"
            >
              MATRICULE-SE COM BOLETO
            </Button>
          </ModalBankSlip>
        )}
        {/* <ModalBankSlip
          contactOrigin={AGENDOR_CONTACT_ORIGIN.QUERO_ME_INSCREVER}
          courseAmount={graduation.amount}
          courseId={graduation.reference}
          courseName={graduation.name}
          title="Possui interesse no Boleto Parcelado? Entraremos em contato."
        >
          <Button
            className="mt-10 h-12 rounded-full border border-black text-base text-black"
            variant="outline"
          >
            MATRICULE-SE COM BOLETO
          </Button>
        </ModalBankSlip> */}
      </div>

      {/* <ModalLayout
        enrolmentData={enrolmentData}
        externalIdProduct={graduation.external_id}
        notLoggedIn={notLoggedIn}
        productType={graduation?.product_type}
        referenceProduct={graduation.reference}
        setNotLoggedIn={setNotLoggedIn}
      /> */}
    </div>
  )
}
